/**
 * DateTimeField - keyboard-first date + time entry.
 * ---------------------------------------------------------------------------
 * Zero runtime dependencies beyond React + ReactDOM.
 *
 * WHY THIS EXISTS
 * A booking agent should be able to Tab into the field and type
 * "06082026 1040a" without ever touching the mouse. Native <select> triples
 * plus a click-only calendar grid make that impossible; this does not.
 *
 * KEYBOARD MAP
 *   Tab / Shift+Tab .... move between fields - the whole date+time is ONE stop
 *   0-9 ................ type into the current segment, auto-advances when full
 *   / : - . or Space ... jump to the next segment manually
 *   Left / Right ....... previous / next segment
 *   Home / End ......... first / last segment
 *   Up / Down .......... increment / decrement the focused segment (wraps)
 *   Shift+Up/Down ...... finer step on minutes (1 min), coarser on year (10 yr)
 *   PageUp / PageDown .. +/- 1 week on a date segment, +/- 1 hour on a time one
 *   A / P .............. set AM / PM from anywhere in the field
 *   T .................. today (keeps the time)   N ... now (date + time)
 *   Backspace / Delete . clear the focused segment
 *   Ctrl/Cmd+V ......... paste "06/08/2026 10:40 AM", "2026-08-06 22:40", etc.
 *   Alt+Down or F4 ..... open the calendar (optional - never required)
 *   Esc ................ close the calendar, focus returns to where it was
 *
 * TWO WAYS TO CONTROL IT
 *   value / onChange ......... a JS Date. onChange only fires once every
 *                              segment is filled, so a half-typed value is
 *                              reported as null.
 *   parts / onPartsChange .... the raw {day,month,year,hour,minute,meridiem}.
 *                              Use this when the date and the time are stored
 *                              separately and either may legitimately be set
 *                              without the other. StringDateTimeField below
 *                              wraps this for the "YYYY-MM-DD" + "HH:mm"
 *                              string pairs used across the Travel ERP.
 *
 * The calendar is rendered through a portal on document.body: these fields sit
 * inside panels and table containers with `overflow: auto`, which would clip
 * an inline popup.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import "./DateTimeField.css";

/* ------------------------------------------------------------------ helpers */

const pad = (n, len = 2) =>
  String(Math.max(0, Number(n) || 0)).padStart(len, "0");
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
/** m is 1-based. */
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) =>
  new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + n,
    d.getHours(),
    d.getMinutes(),
  );
const isSameDay = (a, b) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DOW_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const DOW_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const EMPTY = {
  day: null,
  month: null,
  year: null,
  hour: null,
  minute: null,
  meridiem: null,
};

const serializeParts = (p) =>
  `${p.day}|${p.month}|${p.year}|${p.hour}|${p.minute}|${p.meridiem}`;

let uid = 0;
const useUid = () => useMemo(() => `dtf-${++uid}`, []);

function dateToParts(d, hour12) {
  if (!d || Number.isNaN(d.getTime())) return { ...EMPTY };
  const h24 = d.getHours();
  return {
    day: d.getDate(),
    month: d.getMonth() + 1,
    year: d.getFullYear(),
    hour: hour12 ? (h24 % 12 === 0 ? 12 : h24 % 12) : h24,
    minute: d.getMinutes(),
    meridiem: h24 < 12 ? "AM" : "PM",
  };
}

function partsToDate(p, hour12) {
  const { day, month, year, hour, minute, meridiem } = p;
  if (
    day == null ||
    month == null ||
    year == null ||
    hour == null ||
    minute == null
  )
    return null;
  if (hour12 && !meridiem) return null;
  let h = hour;
  if (hour12) {
    h = hour % 12;
    if (meridiem === "PM") h += 12;
  }
  return new Date(
    year,
    month - 1,
    Math.min(day, daysInMonth(year, month)),
    h,
    minute,
    0,
    0,
  );
}

function specsFor(hour12) {
  return {
    day: { label: "Day", min: 1, max: 31, len: 2, ph: "dd" },
    month: { label: "Month", min: 1, max: 12, len: 2, ph: "mm" },
    year: { label: "Year", min: 1, max: 9999, len: 4, ph: "yyyy" },
    hour: {
      label: "Hour",
      min: hour12 ? 1 : 0,
      max: hour12 ? 12 : 23,
      len: 2,
      ph: "hh",
    },
    minute: { label: "Minute", min: 0, max: 59, len: 2, ph: "mm" },
    meridiem: { label: "AM or PM", ph: "--" },
  };
}

/** Builds the visual token list: segment keys interleaved with separators. */
function buildTokens(dateOrder, hour12, showTime) {
  const order =
    dateOrder === "MDY"
      ? ["month", "day", "year"]
      : dateOrder === "YMD"
        ? ["year", "month", "day"]
        : ["day", "month", "year"];
  const sep = dateOrder === "YMD" ? "-" : "/";
  const tokens = [];
  order.forEach((key, i) => {
    tokens.push({ key });
    if (i < order.length - 1) tokens.push({ sep });
  });
  if (showTime) {
    // Wider gap between the date block and the time block.
    tokens.push({ sep: " ", wide: true });
    tokens.push({ key: "hour" }, { sep: ":" }, { key: "minute" });
    if (hour12)
      tokens.push({ sep: " ", wide: true }, { key: "meridiem" });
  }
  return tokens;
}

/**
 * Lenient parser for pasted text. Handles the shapes reservation staff
 * actually copy out of e-mails and PNRs.
 */
function parsePasted(text, { dateOrder, hour12 }) {
  const raw = String(text).trim();
  if (!raw) return null;

  let y = null,
    mo = null,
    d = null,
    h = null,
    mi = null,
    mer = null;

  // ISO-ish first: 2026-08-06 or 2026/08/06
  let m = raw.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) {
    y = +m[1];
    mo = +m[2];
    d = +m[3];
  } else {
    m = raw.match(/(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
    if (m) {
      const a = +m[1],
        b = +m[2];
      y = +m[3];
      if (y < 100) y += y < 70 ? 2000 : 1900;
      if (dateOrder === "MDY") {
        mo = a;
        d = b;
      } else {
        d = a;
        mo = b;
      }
      // Self-correct an unambiguous mismatch (e.g. 13/08 pasted into MDY).
      if (mo > 12 && d <= 12) {
        const t = mo;
        mo = d;
        d = t;
      }
    } else {
      // "6 Aug 2026" / "Aug 6 2026"
      const nameIdx = MONTHS.findIndex((n) =>
        new RegExp(n.slice(0, 3), "i").test(raw),
      );
      if (nameIdx >= 0) {
        mo = nameIdx + 1;
        const nums = raw.match(/\b(\d{1,4})\b/g) || [];
        const dayCand = nums.find((n) => +n >= 1 && +n <= 31);
        const yearCand = nums.find((n) => n.length === 4);
        if (dayCand) d = +dayCand;
        if (yearCand) y = +yearCand;
      }
    }
  }

  const tm = raw.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp])?\.?[Mm]?/);
  if (tm) {
    h = +tm[1];
    mi = +tm[2];
    if (tm[3]) mer = tm[3].toUpperCase() === "A" ? "AM" : "PM";
  }
  if (mer == null && h != null && hour12) mer = h >= 12 ? "PM" : "AM";
  if (hour12 && h != null) h = h % 12 === 0 ? 12 : h % 12;

  if (d == null && mo == null && y == null && h == null) return null;

  const out = {};
  if (d != null) out.day = clamp(d, 1, 31);
  if (mo != null) out.month = clamp(mo, 1, 12);
  if (y != null) out.year = clamp(y, 1, 9999);
  if (h != null) out.hour = clamp(h, hour12 ? 1 : 0, hour12 ? 12 : 23);
  if (mi != null) out.minute = clamp(mi, 0, 59);
  if (mer != null) out.meridiem = mer;
  return out;
}

/** "6 Aug 2026" for a midnight bound, "6 Aug 2026, 10:40 am" when time matters. */
function describeBound(d, hour12) {
  const date = `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
  if (d.getHours() === 0 && d.getMinutes() === 0) return date;
  const time = hour12
    ? `${d.getHours() % 12 === 0 ? 12 : d.getHours() % 12}:${pad(d.getMinutes())} ${d.getHours() < 12 ? "am" : "pm"}`
    : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${date}, ${time}`;
}

function formatSpoken(p, hour12) {
  const d = partsToDate(p, hour12);
  if (!d) return "incomplete";
  const time = hour12
    ? `${p.hour}:${pad(p.minute)} ${p.meridiem}`
    : `${pad(p.hour)}:${pad(p.minute)}`;
  return `${DOW_LONG[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${time}`;
}

/* ---------------------------------------------------------------- component */

export default function DateTimeField({
  label,
  value, // Date | null - controlled when provided
  defaultValue = null,
  onChange, // (Date | null) => void
  parts: partsProp, // raw parts - controlled when provided
  onPartsChange, // (parts) => void
  min, // Date - inclusive lower bound
  max, // Date - inclusive upper bound
  hour12 = true,
  minuteStep = 5, // up/down step on the minute segment; Shift = 1
  dateOrder = "DMY", // 'DMY' | 'MDY' | 'YMD'
  showTime = true,
  weekStartsOn = 1, // 0 = Sunday, 1 = Monday
  firstDayOfWeekLabel,
  autoFillYear = true, // typing day+month fills the year for you
  preferFuture = true, // ...and rolls to next year if that date has passed
  showCalendarButton = true,
  helpText,
  errorText, // external error takes priority over internal
  disabled = false,
  required = false,
  name,
  id: idProp,
  className = "",
  size = "medium", // 'small' | 'medium' - matches MUI density
  onBlur,
}) {
  const baseId = useUid();
  const id = idProp || baseId;
  const specs = useMemo(() => specsFor(hour12), [hour12]);
  const tokens = useMemo(
    () => buildTokens(dateOrder, hour12, showTime),
    [dateOrder, hour12, showTime],
  );
  const segKeys = useMemo(
    () => tokens.filter((t) => t.key).map((t) => t.key),
    [tokens],
  );

  const [parts, setParts] = useState(() =>
    partsProp
      ? { ...EMPTY, ...partsProp }
      : dateToParts(value !== undefined ? value : defaultValue, hour12),
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const [focusWithin, setFocusWithin] = useState(false);
  const [buffer, setBuffer] = useState({ seg: null, text: "", t: 0 });
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() =>
    startOfDay(value || defaultValue || new Date()),
  );
  const [live, setLive] = useState("");
  const [rangeError, setRangeError] = useState("");
  const [popPos, setPopPos] = useState({ top: 0, left: 0 });

  const segRefs = useRef([]);
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const popRef = useRef(null);
  const returnFocusTo = useRef(0);
  const lastEmitted = useRef("init");
  const lastPartsEmitted = useRef("init");
  const pendingFocus = useRef(null);

  const currentDate = useMemo(() => partsToDate(parts, hour12), [parts, hour12]);

  /* --- controlled-value sync (only when the outside really differs) ------- */
  const valueKey =
    value === undefined ? undefined : value ? value.getTime() : null;
  useEffect(() => {
    if (valueKey === undefined) return;
    const mine = currentDate ? currentDate.getTime() : null;
    if (valueKey !== mine)
      setParts(dateToParts(valueKey == null ? null : new Date(valueKey), hour12));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  /* --- controlled-parts sync --------------------------------------------- */
  const partsKey = partsProp
    ? serializeParts({ ...EMPTY, ...partsProp })
    : undefined;
  useEffect(() => {
    if (partsKey === undefined) return;
    if (partsKey === serializeParts(parts)) return;
    setParts({ ...EMPTY, ...partsProp });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partsKey]);

  /* --- emit changes ------------------------------------------------------- */
  useEffect(() => {
    const key = currentDate ? currentDate.getTime() : null;
    if (lastEmitted.current === "init") {
      lastEmitted.current = key;
      return;
    }
    if (key === lastEmitted.current) return;
    lastEmitted.current = key;
    if (onChange) onChange(currentDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  useEffect(() => {
    const key = serializeParts(parts);
    if (lastPartsEmitted.current === "init") {
      lastPartsEmitted.current = key;
      return;
    }
    if (key === lastPartsEmitted.current) return;
    lastPartsEmitted.current = key;
    if (onPartsChange) onPartsChange(parts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts]);

  /* --- min / max validation ---------------------------------------------- */
  useEffect(() => {
    if (!currentDate) {
      setRangeError("");
      return;
    }
    if (min && currentDate < min) {
      setRangeError(`Must be on or after ${describeBound(min, hour12)}.`);
    } else if (max && currentDate > max) {
      setRangeError(`Must be on or before ${describeBound(max, hour12)}.`);
    } else {
      setRangeError("");
    }
  }, [currentDate, min, max, hour12]);

  /* --- focus plumbing ---------------------------------------------------- */
  const focusSeg = useCallback(
    (i) => {
      const next = clamp(i, 0, segKeys.length - 1);
      setActiveIdx(next);
      setBuffer({ seg: null, text: "", t: 0 });
      pendingFocus.current = next;
    },
    [segKeys.length],
  );

  useLayoutEffect(() => {
    if (pendingFocus.current == null) return;
    const el = segRefs.current[pendingFocus.current];
    pendingFocus.current = null;
    if (el) el.focus();
  });

  /* --- part mutation ----------------------------------------------------- */
  const commit = useCallback(
    (patch, opts = {}) => {
      setParts((prev) => {
        let next = { ...prev, ...patch };

        if (
          autoFillYear &&
          opts.autoYear !== false &&
          next.year == null &&
          next.day != null &&
          next.month != null
        ) {
          const today = startOfDay(new Date());
          let y = today.getFullYear();
          if (preferFuture) {
            const cand = new Date(
              y,
              next.month - 1,
              Math.min(next.day, daysInMonth(y, next.month)),
            );
            if (cand < today) y += 1;
          }
          next.year = y;
        }
        if (next.year != null && next.month != null && next.day != null) {
          next.day = Math.min(next.day, daysInMonth(next.year, next.month));
        }
        if (opts.announce !== false) {
          const spoken = formatSpoken(next, hour12);
          setLive(spoken === "incomplete" ? "" : spoken);
        }
        return next;
      });
    },
    [autoFillYear, preferFuture, hour12],
  );

  const maxForDay = useCallback((p) => {
    if (p.month == null) return 31;
    return daysInMonth(p.year ?? new Date().getFullYear(), p.month);
  }, []);

  /* --- typing ------------------------------------------------------------ */
  const typeDigit = useCallback(
    (seg, digit) => {
      if (seg === "meridiem") {
        // 1 -> AM, 2 -> PM. Cheap muscle-memory shortcut on a numeric keypad.
        if (digit === "1" || digit === "2")
          commit({ meridiem: digit === "1" ? "AM" : "PM" });
        return;
      }
      const spec = specs[seg];
      const now = Date.now();
      const fresh = buffer.seg === seg && now - buffer.t < 1500;
      let text = (fresh ? buffer.text : "") + digit;
      if (text.length > spec.len) text = digit;

      const segMax = seg === "day" ? maxForDay(parts) : spec.max;
      let n = parseInt(text, 10);
      if (n > segMax) {
        text = digit;
        n = parseInt(digit, 10);
      }
      if (n > segMax) n = segMax;

      const full = text.length >= spec.len || n * 10 > segMax;
      setBuffer({ seg, text, t: now });
      // Only auto-fill the year once the segment is complete - a lone "1" in
      // the month is January for a moment, and would otherwise lock in a year
      // computed for the wrong month.
      commit({ [seg]: n >= spec.min ? n : null }, { autoYear: full });

      if (full && n >= spec.min) {
        setBuffer({ seg: null, text: "", t: 0 });
        if (activeIdx < segKeys.length - 1) focusSeg(activeIdx + 1);
      }
    },
    [
      specs,
      buffer,
      parts,
      maxForDay,
      commit,
      activeIdx,
      segKeys.length,
      focusSeg,
    ],
  );

  /* --- stepping ---------------------------------------------------------- */
  const step = useCallback(
    (seg, dir, shift) => {
      if (seg === "meridiem") {
        commit({ meridiem: parts.meridiem === "PM" ? "AM" : "PM" });
        return;
      }
      const spec = specs[seg];
      const segMax = seg === "day" ? maxForDay(parts) : spec.max;

      if (parts[seg] == null) {
        // First press on an empty segment: seed it from "now", don't step past it.
        const seed = dateToParts(new Date(), hour12)[seg];
        commit({ [seg]: clamp(seed, spec.min, segMax) });
        return;
      }

      const cur = parts[seg];
      let next;
      if (seg === "minute" && !shift && minuteStep > 1) {
        next =
          dir > 0
            ? Math.floor(cur / minuteStep) * minuteStep + minuteStep
            : Math.ceil(cur / minuteStep) * minuteStep - minuteStep;
      } else {
        const amount = seg === "year" ? (shift ? 10 : 1) : 1;
        next = cur + dir * amount;
      }
      if (next > segMax) next = spec.min;
      if (next < spec.min) next = segMax;
      commit({ [seg]: next });
    },
    [specs, parts, maxForDay, commit, hour12, minuteStep],
  );

  /** PageUp/Down: +/-1 week on date segments, +/-1 hour on time segments. */
  const bigStep = useCallback(
    (seg, dir, shift) => {
      const isDateSeg = seg === "day" || seg === "month" || seg === "year";
      if (isDateSeg) {
        const y = parts.year ?? new Date().getFullYear();
        const mo = parts.month ?? new Date().getMonth() + 1;
        const d = parts.day ?? new Date().getDate();
        const base = new Date(y, mo - 1, Math.min(d, daysInMonth(y, mo)));
        const moved = shift
          ? new Date(
              base.getFullYear() + dir,
              base.getMonth(),
              Math.min(
                base.getDate(),
                daysInMonth(base.getFullYear() + dir, base.getMonth() + 1),
              ),
            )
          : addDays(base, dir * 7);
        commit({
          day: moved.getDate(),
          month: moved.getMonth() + 1,
          year: moved.getFullYear(),
        });
        return;
      }
      // Time: shift by a whole hour, carrying AM/PM.
      if (parts.hour == null) {
        step(seg, dir, shift);
        return;
      }
      if (hour12) {
        const h24 = (parts.hour % 12) + (parts.meridiem === "PM" ? 12 : 0);
        const n = (h24 + dir + 24) % 24;
        commit({
          hour: n % 12 === 0 ? 12 : n % 12,
          meridiem: n < 12 ? "AM" : "PM",
        });
      } else {
        commit({ hour: (parts.hour + dir + 24) % 24 });
      }
    },
    [parts, commit, step, hour12],
  );

  /* --- quick entry ------------------------------------------------------- */
  const quick = useCallback(
    (what) => {
      const now = new Date();
      if (what === "today") {
        commit({
          day: now.getDate(),
          month: now.getMonth() + 1,
          year: now.getFullYear(),
        });
        setLive("Date set to today");
        return;
      }
      const snapped = new Date(now);
      if (minuteStep > 1)
        snapped.setMinutes(
          Math.round(now.getMinutes() / minuteStep) * minuteStep,
          0,
          0,
        );
      commit(dateToParts(snapped, hour12));
      setLive(
        `Set to now, ${formatSpoken(dateToParts(snapped, hour12), hour12)}`,
      );
    },
    [commit, hour12, minuteStep],
  );

  /* --- calendar popup ---------------------------------------------------- */
  // Positioned against the viewport and rendered on document.body, because
  // these fields live inside containers with `overflow: auto` that would
  // otherwise clip the calendar.
  const updatePopPos = useCallback(() => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const H = 340;
    const W = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= H
        ? rect.bottom + window.scrollY + 4
        : Math.max(8 + window.scrollY, rect.top + window.scrollY - H - 4);
    const spaceRight = window.innerWidth - rect.left;
    const left =
      spaceRight >= W
        ? rect.left + window.scrollX
        : Math.max(8 + window.scrollX, rect.right + window.scrollX - W);
    setPopPos({ top, left });
  }, []);

  const openPicker = useCallback(() => {
    if (disabled) return;
    returnFocusTo.current = activeIdx;
    setCursor(startOfDay(currentDate || new Date()));
    updatePopPos();
    setOpen(true);
  }, [disabled, activeIdx, currentDate, updatePopPos]);

  const closePicker = useCallback(
    (restoreFocus = true) => {
      setOpen(false);
      if (restoreFocus) focusSeg(returnFocusTo.current);
    },
    [focusSeg],
  );

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePopPos();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePopPos]);

  // Move DOM focus onto the cursor day once the popup is painted. Only steal
  // focus when it is outside the popup (first open) or already on a day cell -
  // otherwise clicking the month arrows would yank focus out from under you.
  useEffect(() => {
    if (!open) return;
    const pop = popRef.current;
    if (!pop) return;
    const ae = document.activeElement;
    const shouldMove =
      !ae || !pop.contains(ae) || ae.classList.contains("dtf-day");
    if (!shouldMove) return;
    const el = pop.querySelector('.dtf-day[tabindex="0"]');
    if (el) el.focus();
  }, [open, cursor]);

  // Click outside closes without stealing focus back. The popup is portalled,
  // so it is NOT inside rootRef - it has to be checked separately.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inRoot && !inPop) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  const isDayDisabled = useCallback(
    (d) => {
      if (min && startOfDay(d) < startOfDay(min)) return true;
      if (max && startOfDay(d) > startOfDay(max)) return true;
      return false;
    },
    [min, max],
  );

  const pickDay = useCallback(
    (d) => {
      if (isDayDisabled(d)) {
        setLive(
          `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()} is not available`,
        );
        return;
      }
      const patch = {
        day: d.getDate(),
        month: d.getMonth() + 1,
        year: d.getFullYear(),
      };
      // Give an empty time a sane default so a mouse user still gets a value.
      if (showTime && parts.hour == null) {
        const seed = dateToParts(new Date(), hour12);
        if (minuteStep > 1)
          seed.minute =
            (Math.round(seed.minute / minuteStep) * minuteStep) % 60;
        patch.hour = seed.hour;
        patch.minute = seed.minute;
        patch.meridiem = seed.meridiem;
      }
      commit(patch);
      setOpen(false);
      // Land on the hour so the agent can keep typing the time immediately.
      const hourIdx = segKeys.indexOf("hour");
      focusSeg(showTime && hourIdx >= 0 ? hourIdx : returnFocusTo.current);
    },
    [
      isDayDisabled,
      commit,
      showTime,
      parts.hour,
      hour12,
      minuteStep,
      segKeys,
      focusSeg,
    ],
  );

  /* --- key handling: the field ------------------------------------------- */
  const onControlKeyDown = (e) => {
    if (disabled) return;
    const seg = segKeys[activeIdx];
    if (!seg) return;

    if ((e.altKey && e.key === "ArrowDown") || e.key === "F4") {
      e.preventDefault();
      openPicker();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return; // leave copy/paste + OS keys alone

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        focusSeg(activeIdx + 1);
        return;
      case "ArrowLeft":
        e.preventDefault();
        focusSeg(activeIdx - 1);
        return;
      case "Home":
        e.preventDefault();
        focusSeg(0);
        return;
      case "End":
        e.preventDefault();
        focusSeg(segKeys.length - 1);
        return;
      case "ArrowUp":
        e.preventDefault();
        step(seg, 1, e.shiftKey);
        return;
      case "ArrowDown":
        e.preventDefault();
        step(seg, -1, e.shiftKey);
        return;
      case "PageUp":
        e.preventDefault();
        bigStep(seg, 1, e.shiftKey);
        return;
      case "PageDown":
        e.preventDefault();
        bigStep(seg, -1, e.shiftKey);
        return;
      case "Backspace":
      case "Delete":
        e.preventDefault();
        setBuffer({ seg: null, text: "", t: 0 });
        commit({ [seg]: null }, { autoYear: false });
        setLive(`${specs[seg].label} cleared`);
        return;
      case " ":
        e.preventDefault();
        focusSeg(activeIdx + 1);
        return;
      default:
        break;
    }

    if (
      e.key === "/" ||
      e.key === ":" ||
      e.key === "-" ||
      e.key === "." ||
      e.key === ","
    ) {
      e.preventDefault();
      focusSeg(activeIdx + 1);
      return;
    }
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      typeDigit(seg, e.key);
      return;
    }
    if (hour12 && /^[ap]$/i.test(e.key)) {
      e.preventDefault();
      commit({ meridiem: e.key.toLowerCase() === "a" ? "AM" : "PM" });
      return;
    }
    if (/^t$/i.test(e.key)) {
      e.preventDefault();
      quick("today");
      return;
    }
    if (/^n$/i.test(e.key)) {
      e.preventDefault();
      quick("now");
      return;
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text");
    const patch = parsePasted(text, { dateOrder, hour12 });
    if (!patch) return;
    e.preventDefault();
    commit(patch);
    setLive(`Pasted ${formatSpoken({ ...parts, ...patch }, hour12)}`);
  };

  /* --- key handling: the popup ------------------------------------------- */
  const onPopKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closePicker(true);
      return;
    }
    if (e.key === "Tab") {
      // Keep Tab inside the dialog; Esc is always the way out.
      const focusables = popRef.current
        ? Array.from(
            popRef.current.querySelectorAll(
              'button:not([disabled]):not([tabindex="-1"])',
            ),
          )
        : [];
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const onGridKeyDown = (e) => {
    let next = null;
    switch (e.key) {
      case "ArrowLeft":
        next = addDays(cursor, -1);
        break;
      case "ArrowRight":
        next = addDays(cursor, 1);
        break;
      case "ArrowUp":
        next = addDays(cursor, -7);
        break;
      case "ArrowDown":
        next = addDays(cursor, 7);
        break;
      case "Home":
        next = addDays(cursor, -((cursor.getDay() - weekStartsOn + 7) % 7));
        break;
      case "End":
        next = addDays(cursor, 6 - ((cursor.getDay() - weekStartsOn + 7) % 7));
        break;
      case "PageUp":
        next = e.shiftKey ? shiftYear(cursor, -1) : shiftMonth(cursor, -1);
        break;
      case "PageDown":
        next = e.shiftKey ? shiftYear(cursor, 1) : shiftMonth(cursor, 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        pickDay(cursor);
        return;
      default:
        return;
    }
    e.preventDefault();
    setCursor(next);
    setLive(
      `${DOW_LONG[next.getDay()]} ${next.getDate()} ${MONTHS[next.getMonth()]} ${next.getFullYear()}${
        isDayDisabled(next) ? ", unavailable" : ""
      }`,
    );
  };

  /* --- render ------------------------------------------------------------ */
  const displayFor = (seg) => {
    if (seg === "meridiem") return parts.meridiem ?? specs.meridiem.ph;
    if (buffer.seg === seg && buffer.text)
      return pad(parseInt(buffer.text, 10), specs[seg].len);
    const v = parts[seg];
    if (v == null) return specs[seg].ph;
    return pad(v, specs[seg].len);
  };

  const shownError = errorText || rangeError;
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  const labelId = `${id}-label`;
  const describedBy =
    [helpText ? helpId : null, shownError ? errId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const weeks = useMemo(
    () => buildWeeks(cursor, weekStartsOn),
    [cursor, weekStartsOn],
  );

  const popup = open ? (
    <div
      ref={popRef}
      className="dtf-pop"
      style={{ top: popPos.top, left: popPos.left }}
      role="dialog"
      aria-label="Choose date"
      onKeyDown={onPopKeyDown}
    >
      <div className="dtf-pop-head">
        <button
          type="button"
          className="dtf-nav"
          aria-label="Previous month"
          onClick={() => setCursor(shiftMonth(cursor, -1))}
        >
          &#8249;
        </button>
        <div className="dtf-pop-title">
          {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </div>
        <button
          type="button"
          className="dtf-nav"
          aria-label="Next month"
          onClick={() => setCursor(shiftMonth(cursor, 1))}
        >
          &#8250;
        </button>
      </div>

      <table
        className="dtf-grid"
        role="grid"
        aria-label={`${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
        onKeyDown={onGridKeyDown}
      >
        <thead>
          <tr>
            {Array.from({ length: 7 }, (_, i) => {
              const dow = (i + weekStartsOn) % 7;
              return (
                <th key={dow} scope="col" abbr={DOW_LONG[dow]}>
                  {firstDayOfWeekLabel && i === 0
                    ? firstDayOfWeekLabel
                    : DOW_SHORT[dow]}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((d) => {
                const outside = d.getMonth() !== cursor.getMonth();
                const selected = isSameDay(d, currentDate);
                const isCursor = isSameDay(d, cursor);
                const off = isDayDisabled(d);
                return (
                  <td
                    key={d.toISOString()}
                    role="gridcell"
                    aria-selected={selected || undefined}
                  >
                    {/* Unavailable days use aria-disabled, NOT disabled: a
                        `disabled` button cannot hold focus, so arrowing
                        across a blocked stretch of dates would silently
                        dump focus on <body>. This keeps the grid walkable
                        and lets the reader announce "not available". */}
                    <button
                      type="button"
                      className={`dtf-day${outside ? " is-outside" : ""}${
                        selected ? " is-selected" : ""
                      }${isSameDay(d, new Date()) ? " is-today" : ""}${
                        off ? " is-off" : ""
                      }`}
                      tabIndex={isCursor ? 0 : -1}
                      aria-disabled={off || undefined}
                      aria-label={`${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}${
                        off ? ", not available" : ""
                      }`}
                      aria-current={
                        isSameDay(d, new Date()) ? "date" : undefined
                      }
                      onClick={() => pickDay(d)}
                    >
                      {d.getDate()}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="dtf-pop-foot">
        <button
          type="button"
          className="dtf-btn"
          onClick={() => {
            setParts({ ...EMPTY });
            setLive("Cleared");
            closePicker(true);
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className="dtf-btn"
          onClick={() => {
            quick("today");
            setCursor(startOfDay(new Date()));
          }}
        >
          Today
        </button>
        <button
          type="button"
          className="dtf-btn is-primary"
          onClick={() => closePicker(true)}
        >
          Done
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`dtf${shownError ? " is-invalid" : ""}${
        disabled ? " is-disabled" : ""
      }${size === "small" ? " dtf-sm" : ""} ${className}`.trim()}
      onBlur={(e) => {
        // The popup is portalled, so relatedTarget inside it is NOT inside
        // rootRef - check both or opening the calendar reads as a blur.
        const inRoot =
          rootRef.current && rootRef.current.contains(e.relatedTarget);
        const inPop =
          popRef.current && popRef.current.contains(e.relatedTarget);
        if (!inRoot && !inPop) {
          setFocusWithin(false);
          setBuffer({ seg: null, text: "", t: 0 });
          if (onBlur) onBlur(e);
        }
      }}
      onFocus={() => setFocusWithin(true)}
    >
      {label && (
        <span className="dtf-label" id={labelId}>
          {label}
        </span>
      )}

      <div
        ref={controlRef}
        className={`dtf-control${focusWithin ? " is-focused" : ""}`}
        role="group"
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={describedBy}
        aria-invalid={shownError ? true : undefined}
        aria-required={required || undefined}
        onKeyDown={onControlKeyDown}
        onPaste={onPaste}
        onMouseDown={(e) => {
          // Clicking the padding should land on the first unfilled segment.
          if (e.target === controlRef.current) {
            e.preventDefault();
            const firstEmpty = segKeys.findIndex((k) => parts[k] == null);
            focusSeg(firstEmpty === -1 ? 0 : firstEmpty);
          }
        }}
      >
        {tokens.map((t, i) => {
          if (t.sep)
            return (
              <span
                className={`dtf-sep${t.wide ? " is-wide" : ""}`}
                key={`s${i}`}
                aria-hidden="true"
              >
                {t.sep}
              </span>
            );
          const segIdx = segKeys.indexOf(t.key);
          const spec = specs[t.key];
          const v = parts[t.key];
          const empty =
            t.key === "meridiem" ? parts.meridiem == null : v == null;
          return (
            <span
              key={t.key}
              ref={(el) => {
                segRefs.current[segIdx] = el;
              }}
              className={`dtf-seg${
                empty && !(buffer.seg === t.key && buffer.text)
                  ? " is-empty"
                  : ""
              }`}
              role="spinbutton"
              tabIndex={disabled ? -1 : segIdx === activeIdx ? 0 : -1}
              aria-label={spec.label}
              aria-valuenow={t.key === "meridiem" ? undefined : (v ?? undefined)}
              aria-valuemin={t.key === "meridiem" ? undefined : spec.min}
              aria-valuemax={
                t.key === "meridiem"
                  ? undefined
                  : t.key === "day"
                    ? maxForDay(parts)
                    : spec.max
              }
              aria-valuetext={
                t.key === "meridiem"
                  ? (parts.meridiem ?? "empty")
                  : v == null
                    ? "empty"
                    : t.key === "month"
                      ? MONTHS[v - 1]
                      : String(v)
              }
              onFocus={() => setActiveIdx(segIdx)}
              onMouseDown={(e) => {
                e.preventDefault();
                focusSeg(segIdx);
              }}
              onWheel={(e) => {
                if (segIdx !== activeIdx) return;
                step(t.key, e.deltaY < 0 ? 1 : -1, e.shiftKey);
              }}
            >
              {displayFor(t.key)}
            </span>
          );
        })}

        {showCalendarButton && (
          <button
            type="button"
            className="dtf-toggle"
            tabIndex={-1}
            aria-label={open ? "Close calendar" : "Open calendar"}
            aria-expanded={open}
            aria-haspopup="dialog"
            disabled={disabled}
            onClick={() => (open ? closePicker(true) : openPicker())}
            onKeyDown={(e) => {
              if (e.key === "Escape" && open) {
                e.preventDefault();
                closePicker(true);
              }
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <rect
                x="1.5"
                y="3"
                width="13"
                height="11.5"
                rx="1.75"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path
                d="M1.5 6.5h13M5 1.5V4M11 1.5V4"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      {name && (
        <input
          type="hidden"
          name={name}
          value={currentDate ? currentDate.toISOString() : ""}
        />
      )}

      {helpText && !shownError && (
        <span className="dtf-help" id={helpId}>
          {helpText}
        </span>
      )}
      {shownError && (
        <span className="dtf-error" id={errId}>
          {shownError}
        </span>
      )}

      <span className="dtf-sr" aria-live="polite" aria-atomic="true">
        {live}
      </span>

      {popup && ReactDOM.createPortal(popup, document.body)}
    </div>
  );
}

/* ------------------------------------------------- string-pair adapter ---- */

const ymdToParts = (ymd) => {
  if (!ymd) return { day: null, month: null, year: null };
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return { day: null, month: null, year: null };
  return { day: d, month: m, year: y };
};

const hmToParts = (hm, hour12) => {
  if (!hm) return { hour: null, minute: null, meridiem: null };
  const [rawH, rawM] = String(hm).split(":");
  const h = parseInt(rawH, 10);
  const mi = parseInt(rawM, 10);
  if (Number.isNaN(h) || Number.isNaN(mi))
    return { hour: null, minute: null, meridiem: null };
  if (!hour12) return { hour: h, minute: mi, meridiem: null };
  return {
    hour: h % 12 === 0 ? 12 : h % 12,
    minute: mi,
    meridiem: h < 12 ? "AM" : "PM",
  };
};

const partsToYmd = (p) =>
  p.year != null && p.month != null && p.day != null
    ? `${p.year}-${pad(p.month)}-${pad(p.day)}`
    : "";

const partsToHm = (p, hour12) => {
  if (p.hour == null || p.minute == null) return "";
  if (!hour12) return `${pad(p.hour)}:${pad(p.minute)}`;
  if (!p.meridiem) return "";
  let h = p.hour % 12;
  if (p.meridiem === "PM") h += 12;
  return `${pad(h)}:${pad(p.minute)}`;
};

/**
 * Drop-in replacement for the hand-rolled SingleDateTimePicker / SingleDatePicker
 * copies across the Travel ERP. Same props, same callbacks, so call sites do
 * not change:
 *
 *   date         "YYYY-MM-DD" | ""      onDateChange(str)
 *   time         "HH:mm" | ""           onTimeChange(str)
 *   minDate      "YYYY-MM-DD" | null    maxDate "YYYY-MM-DD" | null
 *   label, showTime, disabled, size
 *
 * Driven at the parts level rather than through a Date, because the date and
 * the time are stored independently here and either may be set on its own -
 * a Date-based value would report a half-filled field as null and wipe it.
 */
export function StringDateTimeField({
  date,
  time,
  onDateChange,
  onTimeChange,
  label,
  minDate = null,
  maxDate = null,
  showTime = true,
  hour12 = true,
  ...rest
}) {
  // A half-filled date (e.g. month cleared) serialises to "", which cannot be
  // turned back into the remaining day/year. Remember the parts we last sent
  // up, and keep using them for as long as the parent echoes our own strings.
  const lastSent = useRef(null);

  const parts = useMemo(() => {
    const s = lastSent.current;
    if (
      s &&
      s.date === (date || "") &&
      (!showTime || s.time === (time || ""))
    )
      return s.parts;
    return {
      ...ymdToParts(date),
      ...(showTime
        ? hmToParts(time, hour12)
        : { hour: null, minute: null, meridiem: null }),
    };
  }, [date, time, showTime, hour12]);

  const min = useMemo(() => {
    if (!minDate) return undefined;
    const p = ymdToParts(minDate);
    return p.year ? new Date(p.year, p.month - 1, p.day) : undefined;
  }, [minDate]);

  const max = useMemo(() => {
    if (!maxDate) return undefined;
    const p = ymdToParts(maxDate);
    return p.year ? new Date(p.year, p.month - 1, p.day, 23, 59, 59) : undefined;
  }, [maxDate]);

  const handleParts = (next) => {
    const nextDate = partsToYmd(next);
    const nextTime = showTime ? partsToHm(next, hour12) : "";
    lastSent.current = { date: nextDate, time: nextTime, parts: next };
    if (nextDate !== (date || "") && onDateChange) onDateChange(nextDate);
    if (showTime && nextTime !== (time || "") && onTimeChange)
      onTimeChange(nextTime);
  };

  return (
    <DateTimeField
      label={label}
      parts={parts}
      onPartsChange={handleParts}
      min={min}
      max={max}
      showTime={showTime}
      hour12={hour12}
      {...rest}
    />
  );
}

/* -------------------------------------------------------- calendar helpers */

function shiftMonth(d, delta) {
  const y = d.getFullYear();
  const m = d.getMonth() + delta;
  const ny = y + Math.floor(m / 12);
  const nm = ((m % 12) + 12) % 12;
  return new Date(ny, nm, Math.min(d.getDate(), daysInMonth(ny, nm + 1)));
}

function shiftYear(d, delta) {
  const ny = d.getFullYear() + delta;
  return new Date(
    ny,
    d.getMonth(),
    Math.min(d.getDate(), daysInMonth(ny, d.getMonth() + 1)),
  );
}

function buildWeeks(cursor, weekStartsOn) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const lead = (first.getDay() - weekStartsOn + 7) % 7;
  const start = addDays(first, -lead);
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let i = 0; i < 7; i++) row.push(addDays(start, w * 7 + i));
    weeks.push(row);
    const last = row[6];
    if (
      w >= 4 &&
      (last.getMonth() !== cursor.getMonth() ||
        last.getDate() >=
          daysInMonth(cursor.getFullYear(), cursor.getMonth() + 1))
    )
      break;
  }
  return weeks;
}

export { parsePasted, partsToDate, dateToParts };
